package com.kanati.admin.features.reports

import androidx.compose.runtime.*
import com.kanati.admin.api.AdminApiClient
import com.kanati.admin.components.*
import com.kanati.admin.state.*
import com.kanati.shared.event.EventDto
import com.kanati.shared.report.TicketSalesReportRow
import com.kanati.shared.report.TicketValidationActivityReportRow
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

fun validationActivityLabel(outcome: String, status: String): String = when (outcome) {
    "GRANTED" -> "Admitted"
    "DENIED" -> if (status in setOf("USED", "ADMITTED")) "Denied · already used" else "Denied"
    "RECONCILED_GRANTED" -> "Reconciled · admitted"
    "RECONCILED_DENIED" -> "Reconciled · denied"
    "PROVISIONAL_GRANTED" -> "Provisional · not final admission"
    "RECONCILIATION_CONFLICT" -> "Conflict · no new admission"
    else -> "Unrecognized outcome · $outcome"
}

@Composable
fun TicketReportsPanel(apiClient: AdminApiClient, district: SelectedDistrictContext, school: SelectedSchoolContext, theme: MaterialThemePalette) {
    val sales = remember { ReadState<List<TicketSalesReportRow>>() }
    val activity = remember { ReadState<List<TicketValidationActivityReportRow>>() }
    val events = remember { ReadState<List<EventDto>>() }
    var eventId by remember { mutableStateOf("") }
    var from by remember { mutableStateOf("") }
    var to by remember { mutableStateOf("") }
    // Only applied filters drive requests; edits never relabel a previous result.
    var filters by remember { mutableStateOf(Triple("", "", "")) }
    var revision by remember { mutableStateOf(0) }
    var eventRevision by remember { mutableStateOf(0) }
    val loading = sales.loading || activity.loading
    LaunchedEffect(eventRevision) {
        events.refresh("Event choices could not be loaded. Retry the event list; all-event reports remain available.") { apiClient.getEvents(district.id, school.id) }
    }
    LaunchedEffect(filters, revision) {
        sales.refresh("Ticket sales could not be loaded. Check access and date filters, then refresh.") {
            apiClient.getTicketSalesReport(district.id, school.id, filters.first, filters.second, filters.third)
        }
    }
    LaunchedEffect(filters, revision) {
        activity.refresh("Validation activity could not be loaded. Check access and date filters, then refresh.") {
            apiClient.getTicketValidationActivityReport(district.id, school.id, filters.first, filters.second, filters.third)
        }
    }
    SectionCard("Ticket reports", theme, description = "Completed sales and recorded gate activity for ${school.name}.", headerActions = {
        ActionButton(if (loading) "Loading..." else "Apply / refresh", { filters = Triple(eventId, from, to); revision++ }, theme, isDisabled = loading, isPrimary = false)
    }) {
        FieldRow {
            Select(attrs = {
                attr("aria-label", "Ticket report event")
                onChange { eventId = it.value.orEmpty() }
                style { padding(12.px); borderRadius(14.px); property("max-width", "100%"); property("font-size", "0.98rem"); property("border", "1px solid ${theme.outline}"); property("background", theme.surface); property("color", theme.onSurface) }
            }) {
                Option("", attrs = { if (eventId.isEmpty()) selected() }) { Text("All school events") }
                events.value.orEmpty().forEach { event ->
                    Option(event.id, attrs = { if (event.id == eventId) selected() }) { Text("${event.name}${if (event.active) "" else " (archived)"}") }
                }
            }
            TextField(theme, "From (optional)", from, inputTypeOverride = "date", onValueChange = { from = it })
            TextField(theme, "To (optional)", to, inputTypeOverride = "date", onValueChange = { to = it })
        }
        events.error?.let {
            StatusBanner(it, theme, BannerTone.Error)
            ActionButton("Retry event list", { eventRevision++ }, theme, isDisabled = events.loading, isPrimary = false)
        }
        if (events.loading) P { Text("Loading event choices...") }
        if (events.value?.isEmpty() == true) P { Text("No events found for this school. Set up an event in Events.") }
        P { Text("Applied: ${events.value?.firstOrNull { it.id == filters.first }?.name ?: filters.first.ifBlank { "All events" }} · ${filters.second.ifBlank { "Any start date" }} to ${filters.third.ifBlank { "Any end date" }}. Dates use ${district.timezone ?: "the district timezone"}; activity is filtered by scan time.") }
        H3 { Text("Ticket sales") }
        P { Text("Completed Consumer and gate sales by event and tier. Payment-path breakdown is not supplied by the backend. Manual issuance and reserved paper stock are not proof of payment.") }
        when {
            sales.loading -> StatusBanner("Loading ticket sales...", theme)
            sales.error != null -> StatusBanner(sales.error!!, theme, BannerTone.Error)
            sales.value?.isEmpty() == true -> StatusBanner("No completed ticket sales match these filters.", theme)
            else -> ReportTable(listOf("Event", "Tier", "Tickets sold", "Gross sales"), sales.value.orEmpty().map {
                listOf(it.eventName, it.ticketTierName, it.ticketsSold.toString(), formatMoney(it.grossSalesAmount))
            }, theme)
        }
        H3 { Text("Validation / check-in activity") }
        P { Text("Backend records only. Legacy check-in and unreconciled local scans are absent. Scan time and recording time are shown as backend timestamps; a denied used/admitted ticket is not another admission.") }
        when {
            activity.loading -> StatusBanner("Loading validation activity...", theme)
            activity.error != null -> StatusBanner(activity.error!!, theme, BannerTone.Error)
            activity.value?.isEmpty() == true -> StatusBanner("No recorded validation activity matches these filters.", theme)
            else -> ReportTable(listOf("Event / tier", "Result", "Ticket status", "Scanned at", "Recorded at"), activity.value.orEmpty().map {
                listOf("${it.eventName} / ${it.ticketTierName ?: "Unknown tier"}", validationActivityLabel(it.outcome, it.ticketStatusAfter), it.ticketStatusAfter, it.scannedAt, it.recordedAt)
            }, theme)
        }
    }
}

@Composable
private fun ReportTable(headers: List<String>, rows: List<List<String>>, theme: MaterialThemePalette) {
    Div({ style { property("overflow-x", "auto") } }) {
        Table({ style { property("width", "100%"); property("border-collapse", "collapse"); property("text-align", "left") } }) {
            Thead {
                Tr {
                    headers.forEach { heading ->
                        Th({ attr("scope", "col"); style { padding(8.px) } }) { Text(heading) }
                    }
                }
            }
            Tbody {
                rows.forEach { row ->
                    Tr {
                        row.forEach { cell ->
                            Td({ style { padding(8.px); property("border-top", "1px solid ${theme.outlineVariant}") } }) { Text(cell) }
                        }
                    }
                }
            }
        }
    }
}
